import React, { useRef, useState } from "react";
import { X } from "lucide-react";

import ModalDialog from "../ModalDialog";

interface ProjectAndReportingPeriodSelectionProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * `reportingPeriod` is always empty: the dialog stopped asking for one, and
	 * the argument stays only so the upload call keeps its shape.
	 */
	onSelect: (projectName: string, reportingPeriod: string) => void;
}

const TITLE_ID = "new-deal-name-title";

/**
 * Names a new deal before its files upload. Styled like the add-files dialogs
 * (DOP-608) so starting a deal and adding to one read as one flow.
 */
const ProjectAndReportingPeriodSelection: React.FC<
	ProjectAndReportingPeriodSelectionProps
> = ({ isOpen, onClose, onSelect }) => {
	const [dealName, setDealName] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const trimmed = dealName.trim();

	if (!isOpen) return null;

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (trimmed) onSelect(trimmed, "");
	};

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onClose} initialFocusRef={inputRef}>
			<form onSubmit={handleSubmit}>
				<div className="flex items-start gap-3">
					<div className="min-w-0 flex-1">
						<h2 id={TITLE_ID} className="text-xl font-semibold text-text-base">
							Name your new deal
						</h2>
						<p className="mt-1 text-sm text-text-muted">
							Use the company or transaction name so you can find it in the
							Deal Room later.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded-md p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
					>
						<X className="h-[18px] w-[18px]" aria-hidden="true" />
					</button>
				</div>

				<label
					htmlFor="new-deal-name"
					className="mt-5 block text-base font-medium text-text-strong"
				>
					Deal name
				</label>
				<input
					id="new-deal-name"
					ref={inputRef}
					type="text"
					value={dealName}
					onChange={(event) => setDealName(event.target.value)}
					placeholder="e.g. Northwind Logistics"
					autoComplete="off"
					className="mt-1.5 w-full rounded-md border border-gray-200 px-3 py-2 text-base text-text-base placeholder:text-text-muted focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
				/>

				<div className="mt-6 flex justify-end gap-2.5">
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!trimmed}
						className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-[#003a3a] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Create deal
					</button>
				</div>
			</form>
		</ModalDialog>
	);
};

export default ProjectAndReportingPeriodSelection;
